import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于clsq - 品牌介绍与联系方式',
  description: '了解clsq品牌的发展历程、核心价值、品牌信誉及联系方式。clsq以创新驱动发展，以品质赢得信赖。',
  url: 'https://clsq.com/about',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://clsq.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://clsq.com/about' },
  ],
};

const milestones = [
  { year: '2016', title: '品牌创立', desc: 'clsq正式成立，以智能科技为核心方向，开启创业征程。' },
  { year: '2018', title: '首款产品发布', desc: '推出第一代智能解决方案，获得市场积极反响，服务首批50家企业客户。' },
  { year: '2020', title: '全国布局', desc: '业务拓展至全国主要城市，团队规模突破200人，年营收首次突破亿元。' },
  { year: '2022', title: '技术突破', desc: '自主研发核心算法取得重大突破，获得多项国家专利，行业影响力显著提升。' },
  { year: '2024', title: '行业领军', desc: '服务客户超过500家，覆盖金融、制造、教育等核心行业，成为行业标杆品牌。' },
  { year: '2026', title: '全球拓展', desc: '启动国际化战略，产品与服务进入海外市场，开启全球化发展新篇章。' },
];

const reputationItems = [
  { icon: 'ri-award-line', value: '500+', label: '服务企业客户' },
  { icon: 'ri-shield-star-line', value: '99.7%', label: '客户满意度' },
  { icon: 'ri-medal-line', value: '50+', label: '行业奖项' },
  { icon: 'ri-timer-flash-line', value: '10年', label: '行业深耕' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9q9gmcnlsngrm4lltug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else if (
        !response.ok ||
        (serverMsg && String(serverMsg).toLowerCase().includes('spam'))
      ) {
        setFormStatus('error');
        setFormMessage(
          typeof serverMsg === 'string' && serverMsg.length < 200
            ? serverMsg
            : '提交失败，请稍后重试或通过其他方式联系我们。'
        );
      } else {
        setFormStatus('error');
        setFormMessage(
          typeof serverMsg === 'string' && serverMsg.length < 200
            ? serverMsg
            : '提交失败，请稍后重试或通过其他方式联系我们。'
        );
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="关于clsq - 品牌介绍与联系方式 | clsq官方"
        description="了解clsq品牌的发展历程、核心价值、品牌信誉及联系方式。clsq以创新驱动发展，以品质赢得信赖，服务超过500家企业客户。"
        keywords="clsq品牌介绍,clsq联系方式,clsq品牌信誉,关于clsq"
        canonicalUrl="https://clsq.com/about"
        schemaData={{ ...aboutSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-16">
          {/* Breadcrumb */}
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pt-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
          </div>

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 max-w-7xl mx-auto pt-10 pb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-primary-100/70 text-primary-700 rounded-full text-xs font-medium mb-4">
                  品牌介绍
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-6">
                  关于clsq
                </h1>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">clsq</strong>是一家专注于智能解决方案与企业服务的科技品牌，自2016年创立以来，始终秉持"创新驱动、品质为本"的核心理念。
                  </p>
                  <p>
                    我们汇聚行业顶尖人才，深耕智能科技领域，构建了涵盖智能解决方案、企业管理系统、数据分析平台、云计算服务、安全解决方案和技术咨询服务的完整产品矩阵。
                  </p>
                  <p>
                    截至目前，<strong className="text-foreground-900">clsq</strong>已累计服务超过<strong className="text-primary-600">500家</strong>企业客户，覆盖金融、制造、教育、电商、医疗等多个行业领域，客户满意度高达<strong className="text-primary-600">99.7%</strong>。
                  </p>
                  <p>
                    <strong className="text-foreground-900">clsq</strong>将持续以技术创新为驱动，以客户价值为导向，努力成为全球领先的智能科技服务品牌。
                  </p>
                </div>
              </div>
              <div className="relative w-full h-80 md:h-96 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20corporate%20headquarters%20building%20interior%20with%20warm%20cream%20and%20beige%20tones%2C%20professional%20team%20collaborating%20in%20open%20workspace%2C%20natural%20light%2C%20elegant%20design%2C%20clean%20aesthetic%2C%20harmonious%20composition%2C%20no%20text&width=1200&height=800&seq=clsq-about-brand&orientation=landscape"
                  alt="clsq品牌形象"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full py-16 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-14">
                发展历程
              </h2>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden lg:block"></div>
                <div className="space-y-8">
                  {milestones.map((m, i) => (
                    <div
                      key={m.year}
                      className={`flex flex-col lg:flex-row items-start gap-6 ${
                        i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${i % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} hidden lg:block`}>
                        {i % 2 === 0 ? (
                          <div className="p-5 bg-background-50 rounded-lg border border-background-200/60">
                            <h3 className="text-lg font-semibold text-foreground-900 mb-1">{m.title}</h3>
                            <p className="text-sm text-foreground-600">{m.desc}</p>
                          </div>
                        ) : (
                          <div className="lg:invisible"></div>
                        )}
                      </div>

                      {/* Dot */}
                      <div className="flex items-center justify-center flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-accent-500 text-background-50 flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {m.year}
                        </div>
                      </div>

                      <div className={`flex-1 ${i % 2 === 0 ? 'lg:text-left' : 'lg:text-right'}`}>
                        {/* Mobile: show always */}
                        <div className="lg:hidden p-5 bg-background-50 rounded-lg border border-background-200/60">
                          <h3 className="text-lg font-semibold text-foreground-900 mb-1">{m.title}</h3>
                          <p className="text-sm text-foreground-600">{m.desc}</p>
                        </div>
                        {/* Desktop */}
                        <div className="hidden lg:block">
                          {i % 2 === 0 ? (
                            <div className="lg:invisible"></div>
                          ) : (
                            <div className="p-5 bg-background-50 rounded-lg border border-background-200/60">
                              <h3 className="text-lg font-semibold text-foreground-900 mb-1">{m.title}</h3>
                              <p className="text-sm text-foreground-600">{m.desc}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section id="reputation" className="w-full py-16 md:py-24 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="text-center mb-14">
                <span className="inline-block px-4 py-1.5 bg-accent-100/80 text-accent-700 rounded-full text-xs font-medium mb-4">
                  品牌信誉
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  用数据说话
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                  clsq以卓越的品质和专业的服务，赢得了广大客户的信赖与认可
                </p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {reputationItems.map((item) => (
                  <div
                    key={item.label}
                    className="text-center p-6 md:p-8 bg-background-100 rounded-lg border border-background-200/60"
                  >
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100/80">
                      <i className={`${item.icon} text-xl text-accent-600`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1">{item.value}</div>
                    <div className="text-sm text-foreground-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="w-full py-16 md:py-24 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 bg-secondary-100/80 text-secondary-700 rounded-full text-xs font-medium mb-4">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  获取专属方案
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                  填写下方表单，我们的专业团队将在24小时内与您取得联系，为您量身定制最适合的解决方案。
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/60 space-y-5"
              >
                {/* Honeypot */}
                <div className="form-company-alt-wrap">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-2">
                      公司名称
                    </label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的公司名称"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-2">
                      电话
                    </label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的联系电话"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                    需求描述 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="请描述您的需求，我们将为您提供针对性的解决方案（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                </div>

                {/* Status Messages */}
                {formStatus === 'success' && (
                  <div className="p-4 bg-accent-100/60 border border-accent-300/50 rounded-md">
                    <p className="text-sm text-accent-800 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line"></i>
                      {formMessage}
                    </p>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 bg-primary-100/60 border border-primary-300/50 rounded-md">
                    <p className="text-sm text-primary-800 flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {formMessage}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3.5 bg-primary-500 text-background-50 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    '提交需求'
                  )}
                </button>
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}